/**
 * Frequency distribution tables: ungrouped (one row per distinct value or category) and
 * grouped (class intervals).
 *
 * ## Grouped tables
 *
 * The textbook construction, made exact:
 *
 * 1. The **unit** is the precision the data are recorded to — 1 for whole numbers,
 *    0.1 for one decimal place. Class limits are written to that precision, and the
 *    **boundaries** sit half a unit outside them (limits 10–19 → boundaries 9.5–19.5),
 *    so every possible recorded value falls in exactly one class.
 * 2. The number of classes k defaults to **Sturges' rule**, k = ⌈1 + log₂ n⌉.
 * 3. The **width** is range ÷ k rounded up to a whole number of units. If that division
 *    is exact, the width goes one unit higher: k classes of exactly range ÷ k would end
 *    one unit short of the maximum. Equivalently, w is the smallest multiple of the unit
 *    with k × w > range.
 * 4. The first class starts at the minimum unless a lower start is chosen (a round
 *    number reads better). The table has as many classes as it takes to reach the
 *    maximum from the start — usually k, one more when an early start shifts the last
 *    value past the k-th class, and fewer when rounding the width up left room to spare.
 *
 * All counting is done in integer units (values × 10^decimals) so a value that sits on
 * a class limit is never pushed across it by binary floating-point noise.
 */

/** Decimals beyond this are treated as measurement noise, not precision. */
const MAX_DECIMALS = 6;
/** A table taller than this is not a summary any more. */
export const MAX_CLASSES = 100;
export const MAX_UNGROUPED_ROWS = 200;

const CATEGORY_SEPARATORS = /[\r\n,;\t]+/;
const CSV_NEEDS_QUOTES = /[",\r\n]/;
const PIPE = /\|/g;
const DOUBLE_QUOTE = /"/g;

export interface Frequencies {
  frequency: number;
  /** Proportion of n, 0–1. */
  relative: number;
  cumulative: number;
  /** Cumulative proportion, 0–1. */
  cumulativeRelative: number;
}

export interface ValueRow extends Frequencies {
  value: number;
}

export interface CategoryRow extends Frequencies {
  category: string;
}

export interface ClassRow extends Frequencies {
  lower: number;
  upper: number;
  lowerBoundary: number;
  upperBoundary: number;
  midpoint: number;
}

function withCumulative<T extends { frequency: number }>(
  rows: T[],
  n: number
): (T & Frequencies)[] {
  let running = 0;
  return rows.map((row) => {
    running += row.frequency;
    return {
      ...row,
      relative: row.frequency / n,
      cumulative: running,
      cumulativeRelative: running / n,
    };
  });
}

/** How many decimal places a value is written with (`2.50` arrives as 2.5 → 1). */
export function decimalsOf(value: number): number {
  if (!Number.isFinite(value) || Number.isInteger(value)) {
    return 0;
  }
  const text = Math.abs(value).toPrecision(15);
  const trimmed = Number.parseFloat(text).toString();
  if (trimmed.includes("e-")) {
    return MAX_DECIMALS;
  }
  const dot = trimmed.indexOf(".");
  return dot === -1 ? 0 : Math.min(MAX_DECIMALS, trimmed.length - dot - 1);
}

/** Sturges (1926): k = ⌈1 + log₂ n⌉. */
export function sturgesClassCount(n: number): number {
  if (n < 1) {
    return 1;
  }
  return Math.ceil(1 + Math.log2(n) - 1e-12);
}

/** One row per distinct value, ascending. */
export function ungroupedFrequencies(values: readonly number[]): ValueRow[] {
  const counts = new Map<number, number>();
  for (const value of values) {
    counts.set(value, (counts.get(value) ?? 0) + 1);
  }
  const rows = [...counts.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([value, frequency]) => ({ value, frequency }));
  return withCumulative(rows, values.length);
}

export function parseCategories(text: string): string[] {
  return text
    .split(CATEGORY_SEPARATORS)
    .map((item) => item.trim())
    .filter((item) => item !== "");
}

export type CategoryOrder = "first" | "alphabetical" | "frequency";

/**
 * One row per category. With `ignoreCase`, "Yes" and "yes" are one category, shown with
 * the spelling it first appeared in.
 */
export function categoryFrequencies(
  items: readonly string[],
  { ignoreCase, order }: { ignoreCase: boolean; order: CategoryOrder }
): CategoryRow[] {
  const groups = new Map<string, { category: string; frequency: number }>();
  for (const item of items) {
    const key = ignoreCase ? item.toLocaleLowerCase() : item;
    const group = groups.get(key);
    if (group) {
      group.frequency += 1;
    } else {
      groups.set(key, { category: item, frequency: 1 });
    }
  }
  const rows = [...groups.values()];
  if (order === "alphabetical") {
    rows.sort((a, b) => a.category.localeCompare(b.category));
  } else if (order === "frequency") {
    // Stable sort: equal counts keep their order of first appearance.
    rows.sort((a, b) => b.frequency - a.frequency);
  }
  return withCumulative(rows, items.length);
}

export interface GroupedOptions {
  /** Number of classes wanted; Sturges' rule when omitted. */
  classCount?: number;
  /** Class width; takes precedence over `classCount`. */
  width?: number;
  /** Lower limit of the first class; the minimum when omitted. */
  start?: number;
}

export type GroupedError =
  | { error: "start-above-minimum"; min: number }
  | { error: "too-many-classes"; classCount: number }
  | { error: "invalid-width" }
  | { error: "invalid-class-count" };

export interface GroupedTable {
  rows: ClassRow[];
  n: number;
  min: number;
  max: number;
  /** max − min. */
  range: number;
  /** Lower limit of the first class. */
  start: number;
  width: number;
  /** The precision of the data: 1, 0.1, 0.01, … */
  unit: number;
  decimals: number;
  /** The k used to derive the width (null when the width was given). */
  requestedClassCount: number | null;
  /** range ÷ k before rounding (null when the width was given). */
  rawWidth: number | null;
  /** True when range ÷ k divided exactly and the width went one unit higher. */
  bumpedExactWidth: boolean;
  usedSturges: boolean;
}

interface UnitScale {
  decimals: number;
  scale: number;
  units: number[];
  minUnits: number;
  maxUnits: number;
}

/** Express the data, the width and the start as integers in the finest unit used. */
function toUnitScale(
  values: readonly number[],
  options: GroupedOptions
): UnitScale {
  let decimals = Math.max(
    options.width === undefined ? 0 : decimalsOf(options.width),
    options.start === undefined ? 0 : decimalsOf(options.start)
  );
  for (const value of values) {
    decimals = Math.max(decimals, decimalsOf(value));
  }
  decimals = Math.min(MAX_DECIMALS, decimals);
  const scale = 10 ** decimals;
  const units = values.map((value) => Math.round(value * scale));
  let minUnits = units[0];
  let maxUnits = units[0];
  for (const value of units) {
    minUnits = Math.min(minUnits, value);
    maxUnits = Math.max(maxUnits, value);
  }
  return { decimals, scale, units, minUnits, maxUnits };
}

interface WidthChoice {
  widthUnits: number;
  requestedClassCount: number | null;
  usedSturges: boolean;
}

function chooseWidth(
  rangeUnits: number,
  n: number,
  scale: number,
  options: GroupedOptions
): WidthChoice | GroupedError {
  if (options.width !== undefined) {
    const widthUnits = Math.round(options.width * scale);
    if (!(widthUnits > 0)) {
      return { error: "invalid-width" };
    }
    return { widthUnits, requestedClassCount: null, usedSturges: false };
  }
  const k = options.classCount ?? sturgesClassCount(n);
  if (!Number.isInteger(k) || k < 1) {
    return { error: "invalid-class-count" };
  }
  return {
    // The smallest whole number of units with k × w > range.
    widthUnits: Math.floor(rangeUnits / k) + 1,
    requestedClassCount: k,
    usedSturges: options.classCount === undefined,
  };
}

function countClasses(
  units: readonly number[],
  startUnits: number,
  widthUnits: number,
  classCount: number,
  scale: number
) {
  const frequencies = new Array<number>(classCount).fill(0);
  for (const value of units) {
    frequencies[Math.floor((value - startUnits) / widthUnits)] += 1;
  }
  return frequencies.map((frequency, index) => {
    const lower = startUnits + index * widthUnits;
    const upper = lower + widthUnits - 1;
    return {
      lower: lower / scale,
      upper: upper / scale,
      lowerBoundary: (lower - 0.5) / scale,
      upperBoundary: (upper + 0.5) / scale,
      midpoint: (lower + upper) / 2 / scale,
      frequency,
    };
  });
}

export function groupedFrequencies(
  values: readonly number[],
  options: GroupedOptions = {}
): GroupedTable | GroupedError | null {
  const n = values.length;
  if (n === 0) {
    return null;
  }
  const { decimals, scale, units, minUnits, maxUnits } = toUnitScale(
    values,
    options
  );
  const startUnits =
    options.start === undefined ? minUnits : Math.round(options.start * scale);
  if (startUnits > minUnits) {
    return { error: "start-above-minimum", min: minUnits / scale };
  }
  const rangeUnits = maxUnits - minUnits;
  const choice = chooseWidth(rangeUnits, n, scale, options);
  if ("error" in choice) {
    return choice;
  }
  const { widthUnits, requestedClassCount, usedSturges } = choice;
  const classCount = Math.floor((maxUnits - startUnits) / widthUnits) + 1;
  if (classCount > MAX_CLASSES) {
    return { error: "too-many-classes", classCount };
  }
  const rows = countClasses(units, startUnits, widthUnits, classCount, scale);
  return {
    rows: withCumulative(rows, n),
    n,
    min: minUnits / scale,
    max: maxUnits / scale,
    range: rangeUnits / scale,
    start: startUnits / scale,
    width: widthUnits / scale,
    unit: 1 / scale,
    decimals,
    requestedClassCount,
    rawWidth:
      requestedClassCount === null
        ? null
        : rangeUnits / scale / requestedClassCount,
    bumpedExactWidth:
      requestedClassCount !== null && rangeUnits % requestedClassCount === 0,
    usedSturges,
  };
}

export function isGroupedError(
  result: GroupedTable | GroupedError
): result is GroupedError {
  return "error" in result;
}

function csvCell(cell: string): string {
  return CSV_NEEDS_QUOTES.test(cell)
    ? `"${cell.replace(DOUBLE_QUOTE, '""')}"`
    : cell;
}

/** RFC 4180 CSV: comma-separated, quoted only where needed, CRLF line ends. */
export function toCsv(
  head: readonly string[],
  rows: readonly (readonly string[])[]
): string {
  return [head, ...rows].map((row) => row.map(csvCell).join(",")).join("\r\n");
}

/** A GitHub-flavoured Markdown table; pipes inside cells are escaped. */
export function toMarkdownTable(
  head: readonly string[],
  rows: readonly (readonly string[])[]
): string {
  const line = (cells: readonly string[]) =>
    `| ${cells.map((cell) => cell.replace(PIPE, "\\|")).join(" | ")} |`;
  return [line(head), line(head.map(() => "---")), ...rows.map(line)].join(
    "\n"
  );
}
